const FRAME_EPSILON = 0.2;

export type PreviewVideoSync =
  | { type: 'seek'; sourceTime: number }
  | { type: 'follow' };

/** Decide whether the single preview element should jump to `sourceTime`.
 *  Playing footage keeps its own clock. A seek happens when the composition
 *  jumps to a different source range, or when paused scrubbing is off by
 *  more than a frame. */
export function decidePreviewVideoSync({
  sourceTime,
  previousSourceTime,
  mediaTime,
  playing,
  pendingSeek,
}: {
  sourceTime: number;
  previousSourceTime: number | null;
  mediaTime: number;
  playing: boolean;
  pendingSeek: number | null;
}): PreviewVideoSync {
  if (!Number.isFinite(sourceTime)) return { type: 'follow' };
  if (pendingSeek !== null && Math.abs(sourceTime - pendingSeek) <= 1 / 30) {
    if (!Number.isFinite(mediaTime) || Math.abs(mediaTime - sourceTime) > 0.12) {
      return { type: 'follow' };
    }
  }
  if (!Number.isFinite(mediaTime)) return { type: 'seek', sourceTime };

  const drift = Math.abs(sourceTime - mediaTime);
  if (!playing) {
    return drift > 1 / 30 ? { type: 'seek', sourceTime } : { type: 'follow' };
  }

  const sourceStep = previousSourceTime === null ? 0 : sourceTime - previousSourceTime;
  if (previousSourceTime !== null && Math.abs(sourceStep) > FRAME_EPSILON) {
    return { type: 'seek', sourceTime };
  }
  if (drift > 0.45) return { type: 'seek', sourceTime };
  return { type: 'follow' };
}
